import { useEffect } from "react";
import { isTossApp } from "@/lib/platform";

/**
 * 토스 인앱 뒤로가기/종료 처리 + history 정합성 보장
 * (costpick TossHistoryFixer 패턴을 React Router 환경에 맞춰 적용)
 *
 * 토스 검수 반려 사유 해결:
 * 1. "공통 내비게이션 바의 좌측 뒤로가기 버튼이 정상 작동하지 않아요"
 *    → graniteEvent.backEvent 리스너 등록 필수
 * 2. "최초 화면에서 내비게이션 바의 뒤로가기 클릭 시 미니앱이 종료되지 않아요"
 *    → window.location.pathname === "/" 일 때 closeView() 호출
 *    → window.close()는 토스 WebView에서 동작하지 않음
 *
 * 추가 안전장치:
 * - history 상태가 꼬이지 않도록 popstate 발생 시 SPA 라우터에 위임
 * - 페이지 로드 시 fade-in (사용자 체감 개선)
 */
export default function TossBackHandler() {
  useEffect(() => {
    if (!isTossApp()) return;

    let unsubBack: (() => void) | null = null;
    let cancelled = false;

    (async () => {
      try {
        const sdk = await import("@apps-in-toss/web-framework");
        if (cancelled) return;

        const { graniteEvent } = sdk;
        // closeView는 native-modules에서 재수출되지만 타입에 노출 안 될 수 있음
        const closeView = (sdk as unknown as { closeView?: () => Promise<void> | void }).closeView;

        const subscription = graniteEvent.addEventListener("backEvent", {
          onEvent: () => {
            // 최초 화면(홈)에서 뒤로가기 → 미니앱 종료
            if (window.location.pathname === "/") {
              if (typeof closeView === "function") {
                closeView();
              } else {
                // SDK에 closeView가 없는 환경 — fallback
                window.close();
              }
              return;
            }

            // 그 외 페이지 → 이전 페이지로 이동 (React Router popstate 자동 처리)
            window.history.back();
          },
        });

        unsubBack = typeof subscription === "function" ? subscription : null;
      } catch {
        // SDK 로드 실패 — 일반 웹 환경
      }
    })();

    // 페이지 로드 시 fade-in (토스 환경 UX 개선)
    document.body.style.opacity = "0";
    document.body.style.transition = "opacity 0.15s ease-in";
    const rafId = requestAnimationFrame(() => {
      document.body.style.opacity = "1";
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (unsubBack) unsubBack();
      document.body.style.opacity = "";
      document.body.style.transition = "";
    };
  }, []);

  return null;
}
